(() => {
  'use strict';

  const product = document.querySelector('.product.is-customily-product');
  if (!product) return;

  const flow = product.querySelector('.tm-customily-flow');
  if (!flow) return;

  const requiredSelector = [
    'input[required]',
    'select[required]',
    'textarea[required]',
    'input[aria-required="true"]',
    'select[aria-required="true"]',
    'textarea[aria-required="true"]',
    '[contenteditable="true"][aria-required="true"]',
  ].join(',');
  const addButtonSelector = [
    '#customily-cart-btn',
    '.product-form__buttons button[name="add"]',
    '.product-form__buttons .product-form__submit',
  ].join(',');
  const stepSelector = '.customily_section, .customily_option, #customily-options';
  const reduceMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false };
  const mediaWrapper = product.querySelector('.product__media-wrapper');

  let toast;
  let toastTimer;
  let requiredSyncFrame;
  let galleryRetryFrame;
  let galleryRetryCount = 0;
  let buttonSyncFrame;
  let reviewValidationFrame;
  let reviewValidationTimer;
  let staleReviewDialogs = new WeakSet();
  let activeSwatchStyleFrame;
  let activeSwatchStyleTimer;
  let optionDescriptionFrame;
  let requiredReviewBlockUntil = 0;
  let requiredScrollFrame;
  let galleryReadyFrame;
  let galleryReadyTimer;
  let galleryReadyCount = 0;
  const customilyFallbackPreviewSelector = [
    '.customily-product-preview',
    '.customily-preview-wrapper',
    '.customily-preview',
    '.customily-container',
    '.cl-canvas-container',
    '.canvas-wrapper',
  ].join(',');
  const customilyFallbackExcludeSelector = [
    '.customily_gallery',
    '.tm-customily-flow',
    '.customily-modal-preview-only',
    '.vue-portal-target',
    '.customily-confirm-dialog',
    '.tm-customily-guide-modal',
    '[role="dialog"]',
  ].join(',');

  const closestSafe = (target, selector) => (target instanceof Element ? target.closest(selector) : null);
  const buttonGroupSelector = '.product-form__buttons';
  const previewButtonSelector = [
    '#customily-preview-button',
    '.customily-preview-button',
    'button[id*="customily-preview"]',
    'button.ld-over-inverse.canSeeCanvas',
    '[data-tm-review-role="preview"]',
    '.tm-review-pill--preview',
  ].join(',');
  const shareButtonSelector = '#customily-share-customization-button';
  const nativeSubmitSelector = 'button[id^="ProductSubmitButton-"].product-form__submit[name="add"]';
  const supportsHasSelector =
    window.CSS && typeof window.CSS.supports === 'function'
      ? window.CSS.supports('selector(label:has(input:checked))')
      : false;
  const activeSwatchSelectorParts = [
    '.customily-swatch input:checked + label',
    'input[type="radio"]:checked + label.needsclick.needsfocus',
    'input[type="checkbox"]:checked + label.needsclick.needsfocus',
    '.customily-swatch label.selected',
    '.customily-swatch label.active',
    '.swatch-container label.selected',
    '.swatch-container label.active',
    'label.needsclick.needsfocus.selected',
    'label.needsclick.needsfocus.active',
    '[aria-selected="true"]',
    '[aria-checked="true"]',
    '[aria-pressed="true"]',
  ];

  if (supportsHasSelector) {
    activeSwatchSelectorParts.push(
      'label:has(input[type="radio"]:checked)',
      'label:has(input[type="checkbox"]:checked)'
    );
  }

  const activeSwatchSelector = activeSwatchSelectorParts.join(',');

  const getButtonGroup = () => flow.querySelector(buttonGroupSelector);
  const getCartButton = () => flow.querySelector('#customily-cart-btn, .customily-cart-btn, button[id*="customily-cart"]');
  const getPreviewButton = () => flow.querySelector(previewButtonSelector);
  const getShareButton = () => flow.querySelector(shareButtonSelector);
  const getNativeSubmitButton = () => flow.querySelector(nativeSubmitSelector);

  const getPreviewTrigger = (target) => {
    const directPreviewButton = closestSafe(target, previewButtonSelector);
    if (directPreviewButton) return directPreviewButton;

    const action = closestSafe(target, 'button, a, [role="button"]');
    if (!action) return null;

    const text = action.textContent?.trim() || '';
    return /revisar\s+(diseñ|disen)/i.test(text) ? action : null;
  };

  const getVariantId = () => flow.querySelector('input[name="id"], .product-variant-id')?.value || '';
  const getProductId = () =>
    product.dataset.productId ||
    flow.querySelector('input[name="product-id"], input[name="product_id"]')?.value ||
    '';

  const getCommonEventParams = () => ({
    product_title:
      product.querySelector('.product__title h1, .product__title .h1')?.textContent?.trim() ||
      document.title.replace(/\s*[|-]\s*.*$/, '').trim(),
    product_id: getProductId(),
    variant_id: getVariantId(),
    page_url: window.location.href,
    event_source: 'timbremania_customily_pdp',
  });

  const TM_ANALYTICS_DEBUG = false;

  function tmDebug(...args) {
    if (!TM_ANALYTICS_DEBUG) return;
    console.log('[TM Analytics]', ...args);
  }

  // Lightweight analytics bridge. Every vendor call is optional and isolated so Customily keeps working.
  const trackTMEvent = (eventName, params = {}) => {
    const eventParams = { ...getCommonEventParams(), ...params };
    const hadDataLayer = Array.isArray(window.dataLayer);

    const sendGtagEvent = (isRetry = false) => {
      try {
        if (typeof window.gtag !== 'function') return false;

        const payload = {
          ...eventParams,
          debug_mode: true,
        };

        tmDebug('GA4 payload', JSON.parse(JSON.stringify(payload)));

        window.gtag('event', eventName, payload);

        tmDebug('gtag sent', {
          event: eventName,
          retry: isRetry,
        });
        return true;
      } catch (error) {
        tmDebug('GA4 event skipped', eventName, error);
        return true;
      }
    };

    if (!sendGtagEvent()) {
      window.setTimeout(() => {
        if (!sendGtagEvent(true)) {
          tmDebug('gtag unavailable after retry', { event: eventName });
        }
      }, 500);
    }

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: eventName,
        ...eventParams,
      });
      tmDebug('dataLayer push', {
        event: eventName,
        gtag_available: typeof window.gtag === 'function',
        dataLayer_available: hadDataLayer,
        pushed: true,
      });
    } catch (error) {
      tmDebug('dataLayer event skipped', eventName, error);
    }

    try {
      if (typeof window.fbq === 'function') window.fbq('trackCustom', eventName, eventParams);
    } catch (error) {
      tmDebug('Meta event skipped', eventName, error);
    }

    try {
      if (typeof window.clarity === 'function') window.clarity('event', eventName);
    } catch (error) {
      tmDebug('Clarity event skipped', eventName, error);
    }

    tmDebug(eventName, eventParams);
  };

  window.trackTMEvent = trackTMEvent;

  let customilyStartedTracked = false;
  let optionChangedTimer;
  let lastOptionChangedParams = {};
  let lastOptionChangedEventKey = '';
  let lastAddAttemptAt = 0;
  let pendingAddToCartVariantId = '';
  let lastAddToCartSuccessKey = '';

  const buttonActionSelector = `${addButtonSelector}, ${previewButtonSelector}, ${shareButtonSelector}`;
  const optionInteractionSelector = [
    '#cl_optionsapp',
    '#customily-options',
    '.customily-options-wrapper',
    '.customily_section',
    '.customily_option',
    '.customily-swatch',
    '.swatch-container',
  ].join(',');
  const optionDescriptionSelector = [
    '.help-text',
    '.cl-help-text',
    '.cl-option-description',
    '.cl-option-help',
    '.customily_option_description',
    '.customily-option-description',
    '.customily-description',
    '.customily_help_text',
    '.customily-help-text',
    '.field__help',
    '.option-helper-text',
    'p',
    'small',
  ].join(',');
  const optionControlSelector = [
    'input',
    'select',
    'textarea',
    'button',
    '[role="button"]',
    '[contenteditable="true"]',
    '.customily-swatch',
    '.swatch-container',
    '.customily-download-button',
    '.cl-file-no-wrap',
    '.cl-container',
  ].join(',');
  const optionDescriptionTextPattern =
    /selecciona|escoge|escribe|sube|utiliza|elige|revisa|ingresa|f[ií]jate|puedes|aparezca|preferencia|datos|tipograf|icono|color exterior|formato|calidad|archivo|espacio permitido/i;

  const isCustomilyInteractionTarget = (target) => {
    const element = target instanceof Element ? target : null;
    if (!element || !flow.contains(element)) return false;
    return Boolean(element.closest('input, select, textarea, button, label, [role="button"], [contenteditable="true"], .customily-swatch, .swatch-container'));
  };

  const trackCustomilyStarted = (target) => {
    if (customilyStartedTracked || !isCustomilyInteractionTarget(target)) return;
    customilyStartedTracked = true;
    trackTMEvent('tm_customily_started');
  };

  const getOptionName = (control) => {
    const option = closestSafe(control, '.customily_option, .customily_section');
    const label = option?.querySelector('label, legend, .customily_option_label, .customily-label, h3, h4');
    return (
      label?.textContent?.replace(/\s+/g, ' ').trim() ||
      control.getAttribute('aria-label') ||
      control.name ||
      control.id ||
      'customily_option'
    );
  };

  const getCheckedLabelValue = (control) => {
    if (!control.matches('input[type="radio"], input[type="checkbox"]')) return '';
    const explicitLabel = Array.from(control.labels || []).find((label) => flow.contains(label));
    const siblingLabel = control.nextElementSibling instanceof HTMLLabelElement ? control.nextElementSibling : null;
    return explicitLabel?.textContent?.trim() || siblingLabel?.textContent?.trim() || '';
  };

  const getOptionValue = (control) => {
    if (control.matches('input[type="file"]')) return control.files?.length ? `${control.files.length} archivo(s)` : '';
    if (control.matches('input[type="radio"], input[type="checkbox"]')) {
      if (!control.checked) return '';
      return control.value || getCheckedLabelValue(control);
    }
    if (control.matches('[contenteditable="true"]')) return control.textContent?.trim() || '';
    if ('value' in control) return String(control.value || '').trim();
    return control.textContent?.trim() || '';
  };

  const scheduleOptionChangedTrack = (target) => {
    const control = closestSafe(target, 'input, select, textarea, [contenteditable="true"], label, [role="button"], .customily-swatch, .swatch-container');
    if (!control || !flow.contains(control)) return;
    if (control.closest(buttonActionSelector)) return;
    if (!control.closest(optionInteractionSelector)) return;

    resetDesignReviewedState();

    const sourceControl = control.matches('label, [role="button"], .customily-swatch, .swatch-container')
      ? control.querySelector('input, select, textarea') || control
      : control;
    const optionName = getOptionName(sourceControl);
    const optionValue = getOptionValue(sourceControl);

    if (!optionName && !optionValue) return;

    const variantId = getVariantId();
    lastOptionChangedParams = {
      option_name: optionName,
      option_value: optionValue,
      variant_id: variantId,
    };

    window.clearTimeout(optionChangedTimer);
    optionChangedTimer = window.setTimeout(() => {
      const eventKey = [
        lastOptionChangedParams.option_name || '',
        lastOptionChangedParams.option_value || '',
        lastOptionChangedParams.variant_id || '',
      ].join('|');

      if (eventKey === lastOptionChangedEventKey) {
        tmDebug('tm_customily_option_changed skipped duplicate', lastOptionChangedParams);
        return;
      }

      lastOptionChangedEventKey = eventKey;
      trackTMEvent('tm_customily_option_changed', lastOptionChangedParams);
    }, 800);
  };

  const isOptionTitleCandidate = (element) =>
    element instanceof HTMLElement &&
    element.matches('.option_name, label, legend, .customily_option_label, .customily-label, h3, h4') &&
    !element.classList.contains('needsclick') &&
    !element.closest('.customily-swatch, .swatch-container, .swatch');

  const getOptionTitleElement = (option) => {
    if (!(option instanceof HTMLElement)) return null;

    const optionName = option.querySelector(':scope > .option_name, :scope .option_name');
    if (isOptionTitleCandidate(optionName)) return optionName;

    const directTitle = Array.from(option.children).find(isOptionTitleCandidate);

    return (
      directTitle ||
      Array.from(
        option.querySelectorAll(
          ':scope > label, :scope > legend, :scope > .customily_option_label, :scope > .customily-label, :scope > h3, :scope > h4'
        )
      ).find(isOptionTitleCandidate) ||
      null
    );
  };

  const isOptionDescriptionElement = (element, option, title) => {
    if (!(element instanceof HTMLElement) || !option.contains(element)) return false;
    if (element === title || element.contains(title)) return false;
    if (element.classList.contains('tm-required-badge')) return false;
    if (element.matches('label, legend, input, select, textarea, button')) return false;
    if (element.closest('.product-form__buttons, .tm-review-gate')) return false;
    if (element.querySelector(optionControlSelector)) return false;

    const text = element.textContent?.replace(/\s+/g, ' ').trim() || '';
    if (text.length < 8 || text.length > 260) return false;

    const signature = `${element.className || ''} ${element.id || ''}`;
    return /help|description|hint|helper|field__help|text-muted|caption/i.test(signature) || optionDescriptionTextPattern.test(text);
  };

  const syncOptionDescriptionPlacement = () => {
    const root = flow.querySelector('#cl_optionsapp') || flow.querySelector('#customily-options') || flow;
    const optionElements = Array.from(root.querySelectorAll('.customily_option'));
    const targets = optionElements.length ? optionElements : Array.from(root.querySelectorAll('.customily_section'));

    targets.forEach((option) => {
      if (!(option instanceof HTMLElement)) return;

      const title = getOptionTitleElement(option);
      if (!(title instanceof HTMLElement)) return;

      const helpTextCandidates = Array.from(option.querySelectorAll('.help-text, small.help-text')).filter(
        (candidate) =>
          candidate instanceof HTMLElement &&
          (optionElements.length
            ? closestSafe(candidate, '.customily_option') === option
            : closestSafe(candidate, '.customily_section') === option)
      );
      const rawCandidates = (helpTextCandidates.length
        ? helpTextCandidates
        : Array.from(option.querySelectorAll(optionDescriptionSelector))).filter(
        (candidate) => isOptionDescriptionElement(candidate, option, title) &&
          (optionElements.length
            ? closestSafe(candidate, '.customily_option') === option
            : closestSafe(candidate, '.customily_section') === option)
      );
      const descriptions = rawCandidates.filter(
        (candidate) => !rawCandidates.some((parent) => parent !== candidate && parent.contains(candidate))
      );

      if (!descriptions.length) return;

      descriptions.forEach((description) => {
        description.classList.add('tm-customily-option-description');
        description.dataset.tmCustomilyDescription = 'true';
      });

      const currentDescriptions = new Set();
      let nextElement = title.nextElementSibling;
      while (nextElement?.classList.contains('tm-customily-option-description')) {
        currentDescriptions.add(nextElement);
        nextElement = nextElement.nextElementSibling;
      }

      if (descriptions.every((description) => currentDescriptions.has(description))) return;

      descriptions
        .slice()
        .reverse()
        .forEach((description) => {
          title.insertAdjacentElement('afterend', description);
        });
    });
  };

  const scheduleOptionDescriptionPlacement = () => {
    window.cancelAnimationFrame(optionDescriptionFrame);
    optionDescriptionFrame = window.requestAnimationFrame(syncOptionDescriptionPlacement);
  };

  const trackAddToCartAttempt = () => {
    const now = Date.now();
    if (now - lastAddAttemptAt < 1200) return;
    lastAddAttemptAt = now;
    trackTMEvent('tm_customily_add_to_cart_attempt');
  };

  const trackAddToCartSuccess = (event = {}) => {
    const variantId = String(event.productVariantId || getVariantId() || '');
    if (pendingAddToCartVariantId && variantId && pendingAddToCartVariantId !== variantId) return;

    const successKey = `${variantId}:${event.cartData?.item_count || ''}`;
    if (successKey && successKey === lastAddToCartSuccessKey) return;

    lastAddToCartSuccessKey = successKey;
    pendingAddToCartVariantId = '';
    trackTMEvent('tm_customily_add_to_cart_success', {
      cart_item_count: event.cartData?.item_count,
    });
  };

  const scheduleSoldOutSync = () => {
    window.requestAnimationFrame(() => {
      syncSoldOutState();
      window.requestAnimationFrame(syncSoldOutState);
      window.setTimeout(syncSoldOutState, 120);
    });
  };

  const syncSoldOutState = () => {
    const nativeSubmit = getNativeSubmitButton();
    const isSoldOut = Boolean(nativeSubmit?.disabled);

    product.classList.toggle('tm-customily-sold-out', isSoldOut);

    [getCartButton(), getPreviewButton(), getShareButton()].forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      if (isSoldOut) {
        button.disabled = true;
        button.dataset.tmSoldOutDisabled = 'true';
        button.setAttribute('aria-disabled', 'true');
        return;
      }

      if (button.dataset.tmSoldOutDisabled === 'true') {
        button.disabled = false;
        delete button.dataset.tmSoldOutDisabled;
        button.removeAttribute('aria-disabled');
      }
    });
  };

  const syncReviewButtonFlow = () => {
    const buttonGroup = getButtonGroup();
    if (!buttonGroup) return;

    const cartButton = getCartButton();
    const previewButton = getPreviewButton();
    const shareButton = getShareButton();
    const reviewed = product.classList.contains('tm-design-reviewed');

    syncSoldOutState();

    buttonGroup.classList.add('tm-review-gate');
    buttonGroup.setAttribute('data-review-state', reviewed ? 'reviewed' : 'pending');

    if (!buttonGroup.querySelector('.tm-cro-trust-note')) {
      const trustNote = document.createElement('p');
      trustNote.className = 'tm-cro-trust-note';
      trustNote.textContent = 'Tu diseño será revisado antes de fabricar.';
      buttonGroup.insertBefore(trustNote, buttonGroup.firstElementChild);
    }

    if (cartButton) {
      cartButton.setAttribute('data-tm-review-role', 'cart');
    }

    if (previewButton) {
      previewButton.setAttribute('data-tm-review-role', 'preview');
      previewButton.setAttribute('aria-label', reviewed ? 'Revisar nuevamente' : 'Revisar diseño');
    }

    if (shareButton) {
      shareButton.setAttribute('data-tm-review-role', 'share');
    }
  };

  const scheduleReviewButtonSync = () => {
    window.cancelAnimationFrame(buttonSyncFrame);
    buttonSyncFrame = window.requestAnimationFrame(syncReviewButtonFlow);
  };

  const markDesignReviewed = () => {
    product.classList.add('tm-design-reviewed');
    scheduleReviewButtonSync();
  };

  const resetDesignReviewedState = () => {
    window.cancelAnimationFrame(reviewValidationFrame);
    window.clearTimeout(reviewValidationTimer);

    if (product.classList.contains('tm-design-reviewed')) {
      product.classList.remove('tm-design-reviewed');
    }

    scheduleReviewButtonSync();
  };

  const getStepColorValue = (step, property, fallback) => {
    if (!(step instanceof Element)) return fallback;
    const value = window.getComputedStyle(step).getPropertyValue(property).trim();
    return value || fallback;
  };

  const syncActiveSwatchStyles = () => {
    flow.querySelectorAll(activeSwatchSelector).forEach((swatch) => {
      if (!(swatch instanceof HTMLElement)) return;

      const step = closestSafe(swatch, '.customily_section');
      if (!step) return;

      const accent = getStepColorValue(step, '--tm-step-accent', '#00b8b0');
      const focus = getStepColorValue(step, '--tm-step-focus', 'rgba(0, 184, 176, 0.13)');
      const checkedBg = getStepColorValue(step, '--tm-step-checked-bg', 'rgba(0, 184, 176, 0.11)');

      swatch.style.setProperty('border', `2px solid ${accent}`, 'important');
      swatch.style.setProperty('border-color', accent, 'important');
      swatch.style.setProperty(
        'background',
        `linear-gradient(135deg, ${checkedBg}, rgba(255, 255, 255, 0.08)), #ffffff`,
        'important'
      );
      swatch.style.setProperty(
        'box-shadow',
        `0 0 0 0.28rem ${focus}, 0 0.7rem 1.8rem rgba(16, 23, 40, 0.06)`,
        'important'
      );
    });
  };

  const scheduleActiveSwatchStyleSync = () => {
    window.cancelAnimationFrame(activeSwatchStyleFrame);
    window.clearTimeout(activeSwatchStyleTimer);

    activeSwatchStyleFrame = window.requestAnimationFrame(() => {
      syncActiveSwatchStyles();
      window.requestAnimationFrame(syncActiveSwatchStyles);
      activeSwatchStyleTimer = window.setTimeout(syncActiveSwatchStyles, 120);
    });
  };

  const clearCustomilyGalleryState = () => {
    product.classList.remove('tm-customily-gallery-ready', 'tm-customily-gallery-pending');
  };

  const setCustomilyGalleryState = (isReady, isPending = !isReady) => {
    const shouldUseCustomilyGallery = window.matchMedia ? window.matchMedia('(min-width: 990px)').matches : true;
    if (!shouldUseCustomilyGallery) {
      clearCustomilyGalleryState();
      return;
    }

    product.classList.toggle('tm-customily-gallery-ready', isReady);
    product.classList.toggle('tm-customily-gallery-pending', !isReady && isPending);
  };

  const isCustomilyPreviewReady = (previewElement) => {
    if (!(previewElement instanceof HTMLElement) || !mediaWrapper?.contains(previewElement)) return false;
    const rect = previewElement.getBoundingClientRect();
    return rect.width > 80 && rect.height > 80;
  };

  const isCustomilyGalleryReady = (customilyGallery) => isCustomilyPreviewReady(customilyGallery);

  const scheduleGalleryReadyCheck = (previewElement, reset = false) => {
    if (reset) galleryReadyCount = 0;
    window.cancelAnimationFrame(galleryReadyFrame);
    window.clearTimeout(galleryReadyTimer);
    galleryReadyFrame = window.requestAnimationFrame(() => {
      const isReady = isCustomilyPreviewReady(previewElement);
      setCustomilyGalleryState(isReady);

      if (isReady) {
        galleryReadyCount = 0;
        return;
      }

      galleryReadyCount += 1;

      if (galleryReadyCount < 80) {
        galleryReadyTimer = window.setTimeout(() => scheduleGalleryReadyCheck(previewElement), 150);
        return;
      }

      clearCustomilyGalleryState();
    });
  };

  const getFallbackPreviewRoot = (element) => {
    if (!(element instanceof HTMLElement)) return null;

    return (
      element.closest('.customily-product-preview') ||
      element.closest('.customily-preview-wrapper') ||
      element.closest('.customily-preview') ||
      element.closest('.customily-container') ||
      element.closest('.cl-canvas-container') ||
      element.closest('.canvas-wrapper')
    );
  };

  const isFallbackPreviewCandidate = (element) => {
    if (!(element instanceof HTMLElement) || !mediaWrapper) return false;
    if (mediaWrapper.contains(element)) return true;
    if (!product.contains(element) && !element.closest('product-info[id^="MainProduct"]')) return false;
    if (element.closest(customilyFallbackExcludeSelector)) return false;
    if (element.querySelector('#cl_optionsapp, .tm-customily-flow')) return false;
    return true;
  };

  const getCustomilyFallbackPreview = () => {
    const candidates = Array.from(document.querySelectorAll(customilyFallbackPreviewSelector))
      .map(getFallbackPreviewRoot)
      .filter(Boolean)
      .filter((element, index, list) => list.indexOf(element) === index)
      .filter(isFallbackPreviewCandidate);

    const externalCandidates = candidates.filter((element) => mediaWrapper && !mediaWrapper.contains(element));
    return externalCandidates.pop() || candidates.find((element) => mediaWrapper?.contains(element)) || null;
  };

  const ensureCustomilyFallbackPreviewPlacement = () => {
    const shouldUseCustomilyGallery = window.matchMedia ? window.matchMedia('(min-width: 990px)').matches : true;
    if (!shouldUseCustomilyGallery) {
      clearCustomilyGalleryState();
      return true;
    }

    const fallbackPreview = getCustomilyFallbackPreview();
    if (!mediaWrapper || !fallbackPreview) return false;

    if (!mediaWrapper.contains(fallbackPreview)) {
      mediaWrapper.appendChild(fallbackPreview);
    }

    fallbackPreview.classList.add('tm-customily-fallback-preview');

    const isReady = isCustomilyPreviewReady(fallbackPreview);
    setCustomilyGalleryState(isReady);
    scheduleGalleryReadyCheck(fallbackPreview, isReady);
    return isReady;
  };

  const ensureCustomilyGalleryPlacement = () => {
    const shouldUseCustomilyGallery = window.matchMedia ? window.matchMedia('(min-width: 990px)').matches : true;
    if (!shouldUseCustomilyGallery) {
      clearCustomilyGalleryState();
      return true;
    }

    const customilyGalleries = Array.from(document.querySelectorAll('.customily_gallery'));
    const externalGallery = customilyGalleries
      .filter((gallery) => mediaWrapper && !mediaWrapper.contains(gallery))
      .pop();
    const customilyGallery =
      externalGallery ||
      customilyGalleries.find((gallery) => mediaWrapper?.contains(gallery)) ||
      customilyGalleries[0];

    if (!mediaWrapper) {
      clearCustomilyGalleryState();
      return false;
    }

    if (!customilyGallery) {
      if (ensureCustomilyFallbackPreviewPlacement()) return true;
      setCustomilyGalleryState(false, true);
      return false;
    }

    if (!mediaWrapper.contains(customilyGallery)) {
      mediaWrapper.appendChild(customilyGallery);
    }

    Array.from(mediaWrapper.querySelectorAll('.customily_gallery')).forEach((gallery) => {
      if (gallery !== customilyGallery) gallery.remove();
    });

    const isReady = isCustomilyGalleryReady(customilyGallery);
    setCustomilyGalleryState(isReady);
    scheduleGalleryReadyCheck(customilyGallery, isReady);
    return isReady;
  };

  const scheduleGalleryPlacementCheck = () => {
    window.cancelAnimationFrame(galleryRetryFrame);
    galleryRetryFrame = window.requestAnimationFrame(() => {
      if (ensureCustomilyGalleryPlacement()) {
        galleryRetryCount = 0;
        return;
      }
      galleryRetryCount += 1;
      if (galleryRetryCount < 160) {
        scheduleGalleryPlacementCheck();
        return;
      }

      clearCustomilyGalleryState();
    });
  };

  const isVisible = (element) => {
    if (!(element instanceof HTMLElement)) return false;
    if (element.hidden || element.closest('[hidden], [aria-hidden="true"], [inert]')) return false;

    const closedDetails = element.closest('details:not([open])');
    if (closedDetails) {
      const summary = Array.from(closedDetails.children).find((child) => child.tagName === 'SUMMARY');
      if (!summary || !summary.contains(element)) return false;
    }

    let current = element;
    while (current && flow.contains(current)) {
      const style = window.getComputedStyle(current);
      if (
        style.display === 'none' ||
        style.visibility === 'hidden' ||
        style.visibility === 'collapse' ||
        Number.parseFloat(style.opacity) === 0
      ) {
        return false;
      }
      current = current.parentElement;
    }

    return element.getClientRects().length > 0;
  };

  const hasVisibleControl = (control) => {
    if (isVisible(control)) return true;
    if (!control.matches('input[type="radio"], input[type="checkbox"]')) return false;

    return Array.from(control.labels || []).some(isVisible);
  };

  const isEligible = (control) => {
    if (!(control instanceof HTMLElement)) return false;
    if (control.matches('input[type="hidden"]') || control.matches(':disabled')) return false;
    if (control.closest('fieldset[disabled]')) return false;
    const step = control.closest(stepSelector);
    if (step && !isVisible(step)) return false;
    return hasVisibleControl(control);
  };

  const isGroupedChoice = (control) =>
    control.matches('input[type="radio"], input[type="checkbox"]') && Boolean(control.name);

  const getChoiceGroup = (control) =>
    Array.from(flow.querySelectorAll('input[type="radio"][name], input[type="checkbox"][name]')).filter(
      (choice) => choice.type === control.type && choice.name === control.name
    );

  const getChoiceGroupKey = (control) => (isGroupedChoice(control) ? `${control.type}:${control.name}` : '');

  const getRequiredLabel = (control) => {
    const option = control.closest('.customily_option');
    const directLabel = option
      ? Array.from(option.children).find((child) => child instanceof HTMLLabelElement)
      : null;

    if (directLabel) return directLabel;
    return Array.from(control.labels || []).find((label) => flow.contains(label)) || null;
  };

  const syncRequiredState = () => {
    const badgeHosts = new Set();
    const processedGroups = new Set();
    flow.querySelectorAll(requiredSelector).forEach((control) => {
      if (!isEligible(control)) return;

      const groupKey = getChoiceGroupKey(control);
      if (groupKey && processedGroups.has(groupKey)) return;
      if (groupKey) processedGroups.add(groupKey);

      const label = getRequiredLabel(control);
      if (label && isVisible(label)) badgeHosts.add(label);
    });

    flow.querySelectorAll('.tm-required-badge').forEach((badge) => {
      if (!badgeHosts.has(badge.parentElement)) badge.remove();
    });

    badgeHosts.forEach((label) => {
      const existingBadge = Array.from(label.children).find((child) => child.classList.contains('tm-required-badge'));
      if (existingBadge) return;

      const badge = document.createElement('span');
      badge.className = 'tm-required-badge';
      badge.setAttribute('aria-hidden', 'true');
      badge.textContent = 'Obligatorio';
      label.appendChild(badge);
    });

    flow.querySelectorAll('.tm-pdp-field-missing').forEach((field) => {
      if (!isEligible(field)) field.classList.remove('tm-pdp-field-missing');
    });

    flow.querySelectorAll('.tm-pdp-step-missing').forEach((step) => {
      if (!isVisible(step) || !step.querySelector('.tm-pdp-field-missing')) {
        step.classList.remove('tm-pdp-step-missing');
      }
    });
  };

  const scheduleRequiredStateSync = () => {
    window.cancelAnimationFrame(requiredSyncFrame);
    requiredSyncFrame = window.requestAnimationFrame(syncRequiredState);
  };

  const isIncomplete = (control) => {
    if (isGroupedChoice(control)) {
      return !getChoiceGroup(control).some((choice) => isEligible(choice) && choice.checked);
    }

    if (control.matches('input[type="radio"], input[type="checkbox"]')) return !control.checked;
    if (control.matches('input[type="file"]')) return !control.files || control.files.length === 0;
    if (control.matches('[contenteditable="true"]')) return (control.textContent || '').trim() === '';
    if ('validity' in control && control.validity.valueMissing) return true;
    return 'value' in control && String(control.value).trim() === '';
  };

  const getFirstIncomplete = () => {
    const checkedChoiceGroups = new Set();

    return Array.from(flow.querySelectorAll(requiredSelector)).find((control) => {
      if (!isEligible(control)) return false;

      const groupKey = getChoiceGroupKey(control);
      if (groupKey) {
        if (checkedChoiceGroups.has(groupKey)) return false;
        checkedChoiceGroups.add(groupKey);
      }

      return isIncomplete(control);
    });
  };

  const getCustomilyFieldWrapper = (field) => {
    if (!(field instanceof HTMLElement)) return field;

    const section = field.closest('.customily_section');
    if (section instanceof HTMLElement) return section;

    const wrapperSelector = [
      '.customily_option',
      '.customily-option',
      '.customily-field',
      '.customily-control',
      '.form__field',
      '.product-form__input',
      'fieldset',
      '#customily-options',
    ].join(',');

    let wrapper = field.closest(wrapperSelector) || field.closest(stepSelector) || field;

    while (
      wrapper.parentElement instanceof HTMLElement &&
      wrapper.parentElement !== flow &&
      wrapper.parentElement.matches('.customily_option, .customily-option, .customily-field')
    ) {
      wrapper = wrapper.parentElement;
    }

    return wrapper;
  };

  const getRequiredFieldScrollTop = (target) => {
    if (!(target instanceof HTMLElement)) return;

    const isMobile = window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
    const desiredTopInViewport = isMobile ? 150 : 120;
    const absoluteTop = target.getBoundingClientRect().top + window.pageYOffset;
    return Math.max(0, absoluteTop - desiredTopInViewport);
  };

  const scrollToCustomilyRequiredField = (target, behavior = 'smooth') => {
    const top = getRequiredFieldScrollTop(target);
    if (typeof top !== 'number') return;

    window.cancelAnimationFrame(requiredScrollFrame);

    if (behavior === 'smooth' && !reduceMotion.matches) {
      const start = window.pageYOffset;
      const distance = top - start;
      const duration = 260;
      const startedAt = window.performance ? window.performance.now() : Date.now();

      const tick = (now) => {
        const elapsed = now - startedAt;
        const progress = Math.min(1, elapsed / duration);
        const eased = 1 - Math.pow(1 - progress, 3);

        window.scrollTo(0, start + distance * eased);

        if (progress < 1) {
          requiredScrollFrame = window.requestAnimationFrame(tick);
        }
      };

      requiredScrollFrame = window.requestAnimationFrame(tick);
      return;
    }

    window.scrollTo({
      top,
      behavior: reduceMotion.matches ? 'auto' : behavior,
    });
  };

  const anchorCustomilyRequiredField = (target) => {
    if (!(target instanceof HTMLElement)) return;

    window.requestAnimationFrame(() => scrollToCustomilyRequiredField(target));
    [80, 220].forEach((delay) => {
      window.setTimeout(() => scrollToCustomilyRequiredField(target, 'auto'), reduceMotion.matches ? 0 : delay);
    });
  };

  const getFirstCustomilyRequiredError = () =>
    Array.from(flow.querySelectorAll('.customily_option:not([visually-hidden])')).find((option) =>
      Array.from(option.querySelectorAll('.customily-required-label span')).some(
        (span) => span.textContent?.trim() && isVisible(span)
      )
    );

  const handleCustomilyRequiredError = () => {
    resetDesignReviewedState();
    showToast();

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const option = getFirstCustomilyRequiredError();
        if (!(option instanceof HTMLElement)) return;

        highlightRequiredField(option);
        scrollToCustomilyRequiredField(option);
        window.setTimeout(() => scrollToCustomilyRequiredField(option, 'auto'), reduceMotion.matches ? 0 : 80);
      });
    });
  };

  const highlightRequiredField = (target) => {
    if (!(target instanceof HTMLElement)) return;

    target.classList.add('tm-required-attention');

    window.setTimeout(
      () => {
        target.classList.remove('tm-required-attention');
      },
      reduceMotion.matches ? 1600 : 2800
    );
  };

  const clearMissingState = () => {
    flow.querySelectorAll('.tm-pdp-field-missing').forEach((field) => field.classList.remove('tm-pdp-field-missing'));
    flow.querySelectorAll('.tm-pdp-step-missing').forEach((step) => step.classList.remove('tm-pdp-step-missing'));
    flow.querySelectorAll('.tm-required-attention').forEach((step) => step.classList.remove('tm-required-attention'));
  };

  const clearFieldMissingState = (control) => {
    if (!(control instanceof HTMLElement)) return;

    if (isGroupedChoice(control)) {
      getChoiceGroup(control).forEach((choice) => {
        choice.classList.remove('tm-pdp-field-missing');
      });
    } else {
      control.classList.remove('tm-pdp-field-missing');
    }

    const step = control.closest(stepSelector);
    if (step && !step.querySelector('.tm-pdp-field-missing')) step.classList.remove('tm-pdp-step-missing');

    const wrapper = getCustomilyFieldWrapper(control);
    if (wrapper instanceof HTMLElement && !wrapper.querySelector('.tm-pdp-field-missing')) {
      wrapper.classList.remove('tm-required-attention');
    }
  };

  const getToast = () => {
    if (toast) return toast;

    toast = document.createElement('div');
    toast.className = 'tm-customily-toast';
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'assertive');
    toast.setAttribute('aria-atomic', 'true');
    toast.hidden = true;
    toast.innerHTML = `
      <span class="tm-customily-toast__mark" aria-hidden="true"></span>
      <span class="tm-customily-toast__copy">
        <strong>Completa los campos requeridos antes de revisar tu dise\u00f1o</strong>
      </span>
    `;
    document.body.appendChild(toast);
    return toast;
  };

  const showToast = () => {
    const currentToast = getToast();
    window.clearTimeout(toastTimer);
    currentToast.hidden = false;
    window.requestAnimationFrame(() => currentToast.classList.add('is-visible'));

    toastTimer = window.setTimeout(() => {
      currentToast.classList.remove('is-visible');
      window.setTimeout(() => {
        currentToast.hidden = true;
      }, reduceMotion.matches ? 0 : 220);
    }, 4600);
  };

  const focusIncomplete = (control) => {
    const step = control.closest(stepSelector);
    const scrollTarget = getCustomilyFieldWrapper(control);

    control.classList.add('tm-pdp-field-missing');
    if (step) step.classList.add('tm-pdp-step-missing');

    highlightRequiredField(scrollTarget);

    if (isVisible(control) && typeof control.focus === 'function') {
      try {
        control.focus({ preventScroll: true });
      } catch (error) {
        // Avoid the browser's native focus scroll here; the custom anchor below controls the final position.
      }
    }

    anchorCustomilyRequiredField(scrollTarget);
  };

  const blockIfIncomplete = (event) => {
    clearMissingState();
    const incomplete = getFirstIncomplete();
    if (!incomplete) return false;

    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

    showToast();
    focusIncomplete(incomplete);
    return true;
  };

  const blockInvalidPreviewAttempt = (event, trackAttempt = false) => {
    const previewButton = getPreviewTrigger(event.target);
    if (!previewButton || previewButton.matches(':disabled, [aria-disabled="true"]')) {
      return false;
    }

    clearMissingState();
    const incomplete = getFirstIncomplete();
    if (!incomplete) return false;

    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

    resetDesignReviewedState();

    const now = Date.now();
    const shouldShowFeedback = now > requiredReviewBlockUntil;
    requiredReviewBlockUntil = now + 1300;

    if (trackAttempt && shouldShowFeedback) trackTMEvent('tm_design_review_clicked');

    if (shouldShowFeedback) {
      showToast();
      focusIncomplete(incomplete);
    } else {
      const wrapper = getCustomilyFieldWrapper(incomplete);
      anchorCustomilyRequiredField(wrapper);
    }

    return true;
  };

  const validationErrorSelector = [
    '[aria-invalid="true"]',
    '.cl-error',
    '.cl-errors',
    '.cl-validation-error',
    '.customily-error',
    '.customily-errors',
    '.validation-error',
    '.field-error',
    '.errors',
    '.form__message',
    '.tm-pdp-field-missing',
    '.tm-pdp-step-missing',
  ].join(',');

  const hasVisibleValidationErrors = () =>
    Array.from(flow.querySelectorAll(validationErrorSelector)).some((element) => {
      if (!isVisible(element)) return false;
      const text = element.textContent?.trim() || '';
      return text || element.matches('[aria-invalid="true"], .tm-pdp-field-missing, .tm-pdp-step-missing');
    });

  const reviewDialogSelector = [
    '.customily-modal-preview-only',
    '.customily-confirm-dialog',
    '.vue-portal-target .cl-preview-wrapper',
  ].join(',');

  const reviewDialogContentSelector = [
    '.cl-preview-image',
    '.cl-use-image',
    '.cl-canvas-container',
    '#customily-cart-btn',
    'canvas',
    'img',
  ].join(',');

  const reviewDialogCloseSelector = [
    '.customily-close-button',
    '.cl-close-button',
    '.close',
    'button[aria-label="Close"]',
    'button[aria-label="Cerrar"]',
    'button[title="Close"]',
    'button[title="Cerrar"]',
  ].join(',');

  const reviewDialogProtectedTapSelector = [
    '.cl-preview-image',
    '.cl-preview-wrapper img',
    '.cl-preview-wrapper canvas',
    '.cl-canvas-container',
    '#customily-cart-btn',
    reviewDialogCloseSelector,
    'button',
    'a',
    'input',
    'select',
    'textarea',
    '[role="button"]',
  ].join(',');

  const isVisibleInDocument = (element) => {
    if (!(element instanceof HTMLElement)) return false;
    if (element.hidden || element.closest('[hidden], [aria-hidden="true"], [inert]')) return false;

    let current = element;
    while (current && current instanceof HTMLElement && current !== document.documentElement) {
      const style = window.getComputedStyle(current);
      if (
        style.display === 'none' ||
        style.visibility === 'hidden' ||
        style.visibility === 'collapse' ||
        Number.parseFloat(style.opacity) === 0
      ) {
        return false;
      }

      current = current.parentElement;
    }

    return element.getClientRects().length > 0;
  };

  const getReviewDialogCandidates = () => Array.from(document.querySelectorAll(reviewDialogSelector));

  const rememberExistingPreviewDialogs = () => {
    staleReviewDialogs = new WeakSet();
    getReviewDialogCandidates().forEach((dialog) => {
      if (isVisibleInDocument(dialog)) staleReviewDialogs.add(dialog);
    });
  };

  const getVisiblePreviewDialog = () =>
    getReviewDialogCandidates().find((dialog) => {
      if (staleReviewDialogs.has(dialog)) return false;
      if (!isVisibleInDocument(dialog)) return false;
      if (dialog.closest('.tm-customily-guide-modal')) return false;
      return Boolean(dialog.querySelector(reviewDialogContentSelector));
    });

  const getVisiblePreviewDialogForTarget = (target) => {
    const targetDialog = closestSafe(target, reviewDialogSelector);
    if (targetDialog && isVisibleInDocument(targetDialog) && !targetDialog.closest('.tm-customily-guide-modal')) {
      return targetDialog;
    }

    return getVisiblePreviewDialog();
  };

  let lastPreviewBackdropCloseAt = 0;

  const closePreviewDialogFromBackdrop = (event) => {
    if (!(event.target instanceof Element)) return false;
    if (event.type === 'click' && Date.now() - lastPreviewBackdropCloseAt < 350) return false;

    const dialog = getVisiblePreviewDialogForTarget(event.target);
    if (!dialog) return false;
    if (closestSafe(event.target, reviewDialogProtectedTapSelector)) return false;

    const closeButton = dialog.querySelector(reviewDialogCloseSelector);
    if (!(closeButton instanceof HTMLElement)) return false;

    closeButton.click();
    lastPreviewBackdropCloseAt = Date.now();
    return true;
  };

  const tmCanMarkDesignAsReviewed = () =>
    !getFirstIncomplete() && !hasVisibleValidationErrors() && Boolean(getVisiblePreviewDialog());

  const scheduleDesignReviewedCheck = (attempt = 0) => {
    window.cancelAnimationFrame(reviewValidationFrame);
    window.clearTimeout(reviewValidationTimer);

    reviewValidationFrame = window.requestAnimationFrame(() => {
      if (tmCanMarkDesignAsReviewed()) {
        markDesignReviewed();
        return;
      }

      if (getFirstIncomplete() || hasVisibleValidationErrors()) {
        resetDesignReviewedState();
        return;
      }

      if (attempt < 60) {
        reviewValidationTimer = window.setTimeout(() => scheduleDesignReviewedCheck(attempt + 1), 150);
        return;
      }

      resetDesignReviewedState();
    });
  };

  const blockIfSoldOut = (event) => {
    if (!product.classList.contains('tm-customily-sold-out')) return false;

    const button = closestSafe(event.target, `${addButtonSelector}, ${previewButtonSelector}, ${shareButtonSelector}`);
    if (!button || !flow.contains(button)) return false;

    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    return true;
  };

  product.addEventListener(
    'click',
    (event) => {
      trackCustomilyStarted(event.target);

      if (blockIfSoldOut(event)) return;

      const button = closestSafe(event.target, addButtonSelector);
      if (!button || !flow.contains(button) || button.matches(':disabled, [aria-disabled="true"]')) return;
      if (blockIfIncomplete(event)) return;

      if (!product.classList.contains('tm-design-reviewed')) {
        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

        const previewButton = getPreviewButton();
        if (previewButton && typeof previewButton.focus === 'function') {
          try {
            previewButton.focus({ preventScroll: true });
          } catch (error) {
            previewButton.focus();
          }
        }
        return;
      }

      trackAddToCartAttempt();
      pendingAddToCartVariantId = getVariantId();
    },
    true
  );

  window.addEventListener(
    window.PointerEvent ? 'pointerdown' : 'mousedown',
    (event) => {
      blockInvalidPreviewAttempt(event, true);
    },
    true
  );

  window.addEventListener(
    'touchstart',
    (event) => {
      blockInvalidPreviewAttempt(event, true);
    },
    { capture: true, passive: false }
  );

  window.addEventListener(
    'click',
    (event) => {
      blockInvalidPreviewAttempt(event, true);
    },
    true
  );

  flow.addEventListener(
    'click',
    (event) => {
      trackCustomilyStarted(event.target);

      if (blockIfSoldOut(event)) return;

      const previewButton = closestSafe(event.target, previewButtonSelector);
      if (previewButton && flow.contains(previewButton) && !previewButton.matches(':disabled, [aria-disabled="true"]')) {
        if (blockInvalidPreviewAttempt(event, true)) return;

        trackTMEvent('tm_design_review_clicked');
        resetDesignReviewedState();

        rememberExistingPreviewDialogs();
        window.setTimeout(scheduleDesignReviewedCheck, 0);
        return;
      }

      const shareButton = closestSafe(event.target, shareButtonSelector);
      if (shareButton && flow.contains(shareButton) && !shareButton.matches(':disabled, [aria-disabled="true"]')) {
        trackTMEvent('tm_share_design_clicked');
        return;
      }

      scheduleOptionChangedTrack(event.target);
      scheduleOptionDescriptionPlacement();
    },
    true
  );

  flow.addEventListener('submit', blockIfIncomplete, true);

  flow.addEventListener(
    'invalid',
    (event) => {
      const control = event.target;
      if (!(control instanceof HTMLElement) || !control.matches(requiredSelector) || !flow.contains(control)) return;

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

      resetDesignReviewedState();

      const incomplete = getFirstIncomplete() || control;
      const now = Date.now();
      if (now <= requiredReviewBlockUntil) {
        anchorCustomilyRequiredField(getCustomilyFieldWrapper(incomplete));
        return;
      }

      requiredReviewBlockUntil = now + 1300;
      showToast();
      focusIncomplete(incomplete);
    },
    true
  );

  flow.addEventListener('focusin', (event) => {
    const step = closestSafe(event.target, stepSelector);
    flow.querySelectorAll('.tm-pdp-step-active').forEach((activeStep) => {
      if (activeStep !== step) activeStep.classList.remove('tm-pdp-step-active');
    });
    if (step) step.classList.add('tm-pdp-step-active');
  });

  flow.addEventListener('focusout', (event) => {
    const step = closestSafe(event.target, stepSelector);
    const focusRemainsInStep = event.relatedTarget instanceof Node && step && step.contains(event.relatedTarget);
    if (step && !focusRemainsInStep) step.classList.remove('tm-pdp-step-active');
  });

  flow.addEventListener('pointerdown', (event) => {
    trackCustomilyStarted(event.target);

    const step = closestSafe(event.target, stepSelector);
    if (!step) return;

    flow.querySelectorAll('.tm-pdp-step-active').forEach((activeStep) => activeStep.classList.remove('tm-pdp-step-active'));
    step.classList.add('tm-pdp-step-active');
    scheduleActiveSwatchStyleSync();
    scheduleOptionDescriptionPlacement();
  });

  flow.addEventListener('click', () => {
    scheduleActiveSwatchStyleSync();
    scheduleOptionDescriptionPlacement();
  });
  flow.addEventListener('input', (event) => {
    trackCustomilyStarted(event.target);
    scheduleOptionChangedTrack(event.target);
    clearFieldMissingState(event.target);
    scheduleActiveSwatchStyleSync();
    scheduleOptionDescriptionPlacement();
  });
  flow.addEventListener('change', (event) => {
    trackCustomilyStarted(event.target);
    scheduleOptionChangedTrack(event.target);
    clearFieldMissingState(event.target);
    scheduleActiveSwatchStyleSync();
    scheduleOptionDescriptionPlacement();
  });
  flow.addEventListener('change', (event) => {
    if (event.target instanceof HTMLInputElement && event.target.matches('.product-variant-id, input[name="id"]')) {
      resetDesignReviewedState();
      scheduleSoldOutSync();
    }
  });
  flow.addEventListener('transitionend', scheduleRequiredStateSync);
  document.addEventListener('variant:change', () => {
    resetDesignReviewedState();
    scheduleSoldOutSync();
  });
  document.addEventListener('product:variant-change', () => {
    resetDesignReviewedState();
    scheduleSoldOutSync();
  });
  document.addEventListener('variantChange', () => {
    resetDesignReviewedState();
    scheduleSoldOutSync();
  });
  document.addEventListener('pointerup', closePreviewDialogFromBackdrop, true);
  document.addEventListener('click', closePreviewDialogFromBackdrop, true);
  window.addEventListener('customily-required-error', handleCustomilyRequiredError, { passive: true });
  window.addEventListener('resize', scheduleRequiredStateSync, { passive: true });
  window.addEventListener('resize', scheduleGalleryPlacementCheck, { passive: true });
  window.addEventListener('orientationchange', scheduleGalleryPlacementCheck, { passive: true });

  syncRequiredState();
  syncReviewButtonFlow();
  scheduleActiveSwatchStyleSync();
  scheduleOptionDescriptionPlacement();
  setCustomilyGalleryState(false);
  ensureCustomilyGalleryPlacement();
  scheduleGalleryPlacementCheck();

  window.setTimeout(scheduleOptionDescriptionPlacement, 250);
  window.setTimeout(scheduleOptionDescriptionPlacement, 600);
  window.setTimeout(scheduleOptionDescriptionPlacement, 1200);
  window.setTimeout(scheduleGalleryPlacementCheck, 250);
  window.setTimeout(scheduleGalleryPlacementCheck, 600);
  window.setTimeout(scheduleGalleryPlacementCheck, 1200);
  window.setTimeout(scheduleGalleryPlacementCheck, 2200);
  window.setTimeout(scheduleGalleryPlacementCheck, 3500);

  if (
    typeof subscribe === 'function' &&
    typeof PUB_SUB_EVENTS !== 'undefined' &&
    PUB_SUB_EVENTS?.cartUpdate
  ) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source !== 'product-form') return;
      trackAddToCartSuccess(event);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleGalleryPlacementCheck, { once: true });
  } else {
    scheduleGalleryPlacementCheck();
  }

  if ('MutationObserver' in window) {
    const requiredObserver = new MutationObserver(scheduleRequiredStateSync);
    requiredObserver.observe(flow, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['required', 'aria-required', 'aria-hidden', 'disabled', 'hidden', 'inert', 'open', 'class', 'style'],
    });

    const galleryObserver = new MutationObserver((mutations) => {
      const shouldCheck = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) =>
            node instanceof Element &&
            (node.matches(`.customily_gallery, ${customilyFallbackPreviewSelector}`) ||
              Boolean(node.querySelector(`.customily_gallery, ${customilyFallbackPreviewSelector}`)))
        )
      );

      if (shouldCheck || document.querySelector(`.customily_gallery, ${customilyFallbackPreviewSelector}`)) {
        scheduleGalleryPlacementCheck();
      }
    });
    galleryObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    const buttonObserver = new MutationObserver(scheduleReviewButtonSync);
    buttonObserver.observe(flow, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'id', 'disabled', 'aria-disabled'],
    });

    const optionDescriptionObserver = new MutationObserver(scheduleOptionDescriptionPlacement);
    optionDescriptionObserver.observe(flow, {
      childList: true,
      subtree: true,
    });
  }
})();

